import React, { useMemo } from 'react';
import { useSet } from 'react-use';
import { PopoverContent } from '../../../../CommonComponents/OPL/Popover/Popover';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { UserActivityFilterOption } from '../../../UserActivity.meta';
import { FilterOptionList } from '../../FilterOptionsList/FilterOptionsList';
import { FilterAccordion } from '../../FilterAccordion/FilterAccordion';
import { useSortFilterDraft } from '../hooks/useSortFilterDraft';
import { useFilterDraft } from '../hooks/useFilterDraft';
import { SortFilterProps } from '../SortFilter';
import { SortBySection } from './SortBySection';
import { SortFilterFooter } from './SortFilterFooter';

export type SortFilterContentProps = Omit<SortFilterProps, 'isOpen' | 'isDataFiltered'>;

type AccordionSection = 'sort' | 'user' | 'activity';

type FilterSectionConfig = {
  id: string;
  section: AccordionSection;
  title: string;
  listTitle: string;
  options: UserActivityFilterOption[];
  zIndex: number;
  filter: ReturnType<typeof useFilterDraft>;
};

export const SortFilterContent = ({
  filterOptions,
  sortOrder,
  selectedActivityFilterIds,
  selectedUserFilterIds,
  onApplyChange,
  onOpenChange,
}: SortFilterContentProps) => {
  const {
    draftSortOrder,
    setDraftSortOrder,
    showUserColumn,
    showActivityColumn,
    userOptions,
    activityOptions,
    userFilter,
    activityFilter,
    isApplyValid,
    isUnSelectValid,
    handleApply,
    handleUnselectAll,
  } = useSortFilterDraft({
    filterOptions,
    sortOrder,
    selectedActivityFilterIds,
    selectedUserFilterIds,
    onApplyChange,
    onOpenChange,
  });

  const initialExpandedSections = useMemo(
    () =>
      new Set<AccordionSection>([
        'sort',
        ...(showUserColumn ? (['user'] as const) : []),
        ...(showActivityColumn ? (['activity'] as const) : []),
      ]),
    [showActivityColumn, showUserColumn],
  );

  const [, { has: isSectionExpanded, toggle: toggleSection }] = useSet(initialExpandedSections);

  const filterSections: FilterSectionConfig[] = [];

  if (showUserColumn) {
    filterSections.push({
      id: 'sort-filter-user',
      section: 'user',
      title: 'Filter By User',
      listTitle: 'User',
      options: userOptions,
      zIndex: 2,
      filter: userFilter,
    });
  }

  if (showActivityColumn) {
    filterSections.push({
      id: 'sort-filter-activity',
      section: 'activity',
      title: 'Filter By Activity Type',
      listTitle: 'Activity Type',
      options: activityOptions,
      zIndex: 3,
      filter: activityFilter,
    });
  }

  return (
    <PopoverContent
      // Wider popover so long filter labels (e.g. "Account Custom Asset Bucket") are not clipped.
      width={400}
      maxHeight={'70vh'}
      css={{
        maxWidth: 'min(640px, 95vw)',
        overflowX: 'hidden',
        overflowY: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
      }}
    >
      <Box flex="1" minHeight={0} overflowY="auto">
        <SortBySection
          sortOrder={draftSortOrder}
          isExpanded={isSectionExpanded('sort')}
          onToggle={() => toggleSection('sort')}
          onSortOrderChange={setDraftSortOrder}
        />

        {filterSections.map(({ id, section, title, listTitle, options, zIndex, filter }) => (
          <FilterAccordion
            key={id}
            id={id}
            title={title}
            isExpanded={isSectionExpanded(section)}
            onToggle={() => toggleSection(section)}
            zIndex={zIndex}
            showTopBorder
          >
            <FilterOptionList
              title={listTitle}
              options={options}
              selectedIds={filter.selectedIds}
              searchTerm={filter.searchTerm}
              debouncedSearchTerm={filter.debouncedSearchTerm}
              onSearchChange={filter.setSearchTerm}
              onSelectedIdsChange={filter.setSelectedIds}
              searchSelectedIds={filter.searchSelectedIds}
              onSearchSelectedIdsChange={filter.setSearchSelectedIds}
            />
          </FilterAccordion>
        ))}
      </Box>

      <SortFilterFooter
        isApplyValid={isApplyValid}
        isUnSelectValid={isUnSelectValid}
        onApply={handleApply}
        onUnselectAll={handleUnselectAll}
      />
    </PopoverContent>
  );
};
